import React, { Fragment, useState } from 'react';
import { Flex, Text, Button, Heading, Box, Card } from 'rebass';
import styled from 'styled-components';
import { Route, NavLink as NavLinkRouter, withRouter } from 'react-router-dom';
import 'react-vertical-timeline-component/style.min.css';
import moment from 'moment-timezone';
import { Mutation, Query, compose } from 'react-apollo';

import withSession from '../lib/withSession';
import gql from 'graphql-tag';
import { GET_PALOMARX_PRODUCTS, GET_VISITS_TOKEN, PALOMA_PRODUCT_TAG_COLORS, PalomaRxProductCard } from './PalomaRxProducts';
import { PalomRxProductTabControl } from './palomaRxProduct/PalomRxProductTabControl';
import { Skeleton } from '../components/loadingSkeleton/Skeleton';
import PillsBottle from '../static/PillsBottle.png';

const A = styled(Text)`
  font-size: 14px;
  font-weight: 400;
  color: #44a5ff;
  text-decoration: underline;
  cursor: pointer;
`;

const GET_PALOMARX_PRODUCT_DETAILS = gql`
  query getPalomaRxProductDetails($productSku: String!) {
    getPalomaRxProductDetails(productSku: $productSku) {
      id
      palomaSku
      name
      category
      imageUrl
      fromPriceCents
      fulfillment
      available
      description
      visitFeeCents
      productMetadata {
        activeIngredients
        inactiveIngredients
        importantSafetyInformation
        commonSideEffects
        warnings
        storage
        manufacturer
        medicationGuideUrl
      }
      faq {
        question
        orderIndex
        answer
      }
      sellingPoints {
        title
        description
        imageUrl
      }
      getStartedSteps {
        stepIndex
        description
        imageUrl
      }
      similarProductIds
    }
  }
`;

const Container = styled(Box)`
  max-width: 1024px;
  width: 100%;
  margin: 0 auto;
  padding-top: 12px;
  z-index: 0;
  @media screen and (max-width: 1100px) {
    padding-left: 8px;
    padding-right: 8px;
  }
  @media screen and (max-width: 52em) {
    padding-left: 8px;
    padding-right: 8px;
  }
`;

const InnerContainer = styled(Flex)`
  max-width: 800px;
  margin: 0 auto;
  flex-direction: column;
  margin-bottom: 60px;
  gap: 20px;
  position: relative;
  @media screen and (max-width: 1100px) {
    max-width: calc(100vw - 300px);
  }
  @media screen and (max-width: 830px) {
    max-width: calc(100vw - 50px);
  }
  @media screen and (max-width: 52em) {
    max-width: 96%;
  }
`;

const Img = styled.img`
  display: initial;
  border-radius: 12px;
  @media screen and (max-width: 52em) {
    display: none;
  }
`;

const ImgTop = styled.img`
  display: initial;
  object-fit: cover;
  border-radius: 12px;
  @media screen and (max-width: 999px) {
    object-fit: contain;
  }
  @media screen and (max-width: 52em) {
    display: none;
  }
`;

const ImgTopMobile = styled.img`
  display: none;
  object-fit: cover;
  border-radius: 12px;
  max-height: 50%;
  @media screen and (max-width: 999px) {
    object-fit: contain;
  }
  @media screen and (max-width: 52em) {
    display: initial;
  }
`;

const LargeDisplay = styled(Flex)`
  @media screen and (max-width: 52em) {
    display: none;
  }
`;

const SmallDisplay = styled(Flex)`
  @media screen and (min-width: 52em) {
    display: none;
  }
`;

const PalomaRxProductDetailsInner = ({ details, categoryBadgeColors }) => {
  return (
    <Flex flexDirection={'column'} style={{ gap: 20 }}>
      <Flex style={{ gap: 20, position: 'relative' }}>
        <Flex flexDirection={'column'} style={{ gap: 12 }} flex={1}>
          <ImgTopMobile src={details.imageUrl} width="100%" />
          <Heading fontSize={'32px'}>{details.name}</Heading>

          {details.category && (
            <SmallDisplay>
              <Card p={'3px 12px'} style={{ background: categoryBadgeColors.bg || '#EBE6A7', maxWidth: 'fit-content', borderRadius: 20 }}>
                <Text fontSize={14} fontWeight={300} color={categoryBadgeColors.text || ''}>
                  {details.category}
                </Text>
              </Card>
            </SmallDisplay>
          )}
          {details.fulfillment && details.fulfillment === 'external_pharmacy' ? (
             <Flex flexDirection={'column'}>
            <Text fontWeight={600}>
              Paid at your pharmacy
            </Text>
            <Text fontSize={13} fontWeight={300}>
              Use your insurance or pay cash
            </Text>
          </Flex>
                            ) : (
          <Flex flexDirection={'column'}>
            <Text fontWeight={600}>
              From ${(details.fromPriceCents / 100).toFixed(2)}
              {details.palomaSku && details.palomaSku !== 'LDN' ? ' / mo' : ''}
            </Text>
            <Text fontSize={13} fontWeight={300}>
              Medication billed only if prescribed
            </Text>
          </Flex>
                            )}
          <Text dangerouslySetInnerHTML={{ __html: details.description }} />

          <Flex flexDirection={'column'}>
            <Text fontWeight={600} fontSize={'14px'}>
              {details.fulfillment && details.fulfillment === 'external_pharmacy' ?'':'+ '}${(details.visitFeeCents / 100).toFixed(0)} clinician review per visit
            </Text>
            <Text fontSize={13} fontWeight={300}>
              A licensed clinician reviews your intake for ${(details.visitFeeCents / 100).toFixed(0)}
            </Text>
          </Flex>
          <Flex style={{ flexDirection: 'row', gap: 20, alignItems: 'center' }}>
            <Mutation
              mutation={GET_VISITS_TOKEN}
              onCompleted={(e) => {
                if (e && e.getAsyncVisitToken) {
                  window.location = `${window._env_.REACT_APP_RX_VISITS_URL}/auth#token=${encodeURIComponent(e.getAsyncVisitToken)}&redirect=${details.palomaSku}`;
                }
              }}
            >
              {(generateToken, { loading }) => (
                <Button
                  onClick={generateToken}
                  disabled={!!loading}
                  variant={'primary'}
                  style={{
                    // padding: '8px 42px',
                    width: 'fit-content',
                    maxWidth: 'fit-content',
                    fontSize: 14,
                    fontWeight: 300,
                  }}
                >
                  Get Started
                </Button>
              )}
            </Mutation>

            <Text style={{ fontWeight: 300, fontSize: '14px', fontStyle: 'italic' }}>{`HSA/FSA Accepted`}</Text>
          </Flex>
        </Flex>

        <ImgTop src={details.imageUrl} width="40%" />
        {details.category && (
          <LargeDisplay style={{ position: 'absolute', top: 12, right: 8 }}>
            <Card
              p={'3px 12px'}
              style={{
                background: categoryBadgeColors.bg || '#EBE6A7',
                maxWidth: 'fit-content',
                borderRadius: 20,
                boxShadow: '0px 4px 20px rgba(0,0,0,0.40)',
              }}
            >
              <Text fontSize={14} fontWeight={300} color={categoryBadgeColors.text || ''}>
                {details.category}
              </Text>
            </Card>
          </LargeDisplay>
        )}
      </Flex>
    </Flex>
  );
};

const PalomaRxProductDetailsInnerSkeleton = () => {
  return (
    <Flex flexDirection={'column'} style={{ gap: 20 }}>
      <Flex style={{ gap: 20 }}>
        <Flex flexDirection={'column'} style={{ gap: 12 }} flex={1}>
          <Skeleton width="220px" height="42px" />
          <Skeleton width="180px" height="24px" />
          <Flex flexDirection={'column'}>
            <Skeleton width="100%" height="16px" />
            <Skeleton width="95%" height="16px" />
            <Skeleton width="100%" height="16px" />
            <Skeleton width="90%" height="16px" />
            <Skeleton width="95%" height="16px" />
            <Skeleton width="70%" height="16px" />
          </Flex>

          <Flex style={{ flexDirection: 'row', gap: 20, alignItems: 'center' }}>
            <Skeleton height="26px" width="111px" borderRadius="13px" baseColor="#344f79" highlightColor="#666e79" style={{ opacity: 0.5 }} />

            <Skeleton width="128px" height="21px" />
          </Flex>
        </Flex>

        <Img src={PillsBottle} width="40%" style={{ objectFit: 'contain', opacity: 0.5 }} />
      </Flex>
    </Flex>
  );
};

const FaqCard = ({ entry }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <Card
      style={{ maxWidth: 'min(95%, 600px)', minWidth: 'min(95%, 600px)', width: 'min(95%, 600px)', display: 'flex', padding: '14px 28px', cursor: 'pointer', borderRadius: '12px' }}
      onClick={(e) => setIsExpanded(!isExpanded)}
    >
      <Flex flexDirection={'column'} flex={1} style={{ gap: 16 }}>
        <Flex style={{ gap: 16 }} alignItems={'center'}>
          <Text flex={1} fontWeight={600}>
            {entry.question}
          </Text>
          <Text fontSize={20}>+</Text>
        </Flex>
        {!!isExpanded && (
          <Flex paddingLeft={12} style={{ borderTop: '1px solid #344f791a' }}>
            <Text dangerouslySetInnerHTML={{ __html: entry.answer }} />
          </Flex>
        )}
      </Flex>
    </Card>
  );
};

const FaqCardSkeleton = ({ entry }) => {
  return (
    <Card style={{ maxWidth: 'min(95%, 600px)', minWidth: 'min(95%, 600px)', width: 'min(95%, 600px)', display: 'flex', padding: '14px 28px', cursor: 'pointer', borderRadius: '12px' }}>
      <Flex flexDirection={'column'} flex={1} style={{ gap: 16 }}>
        <Flex style={{ gap: 16 }} alignItems={'center'}>
          <Skeleton width="150px" height="16px" />
          <Skeleton width="20px" height="20px" />
        </Flex>
      </Flex>
    </Card>
  );
};

const Faq = ({ details }) => {
  if (!details || details.length < 1) return null;
  return (
    <Flex flexDirection={'column'} alignItems={'center'} style={{ gap: 16 }}>
      {details.map((entry) => (
        <FaqCard entry={entry} key={`faq1_${entry.question}`} />
      ))}
    </Flex>
  );
};

const FaqSkeleton = () => {
  return (
    <Flex flexDirection={'column'} alignItems={'center'} style={{ gap: 16 }}>
      {[0, 1, 2, 3].map((entry) => (
        <FaqCardSkeleton key={`faq1_${entry.question}`} />
      ))}
    </Flex>
  );
};

const Faq2 = ({ details }) => {
  if (!details || details.length < 1) return null;
  return (
    <Flex flexDirection={'column'} alignItems={'flex-start'} style={{ gap: 16 }}>
      <Heading>Frequently Asked Questions</Heading>
      {details.map((entry) => (
        <FaqCard entry={entry} key={`faq1_${entry.question}`} />
      ))}
    </Flex>
  );
};

const SellingPoints = ({ sellingPoints }) => {
  if (!sellingPoints || sellingPoints.length < 1) return null;

  return (
    <Flex flexDirection={'column'} style={{ gap: 30 }}>
      {sellingPoints.map((x, i) => (
        <Flex flexDirection={(i + 1) % 2 === 0 ? 'row-reverse' : 'row'} key={x.title} style={{ gap: 20 }}>
          {x.imageUrl ? <Img src={x.imageUrl} width="40%" style={{ objectFit: 'contain' }} /> : <div style={{ width: '40%' }} />}
          <Flex flexDirection={'column'} style={{ gap: 12 }}>
            <Heading>{x.title}</Heading>
            <Text dangerouslySetInnerHTML={{ __html: x.description }} />
          </Flex>
        </Flex>
      ))}
    </Flex>
  );
};

const SellingPointsSkeleton = () => {
  return (
    <Flex flexDirection={'column'} style={{ gap: 30 }}>
      {[0, 1, 2].map((x, i) => (
        <Flex flexDirection={(i + 1) % 2 === 0 ? 'row-reverse' : 'row'} key={`sksps_${i}`} style={{ gap: 20 }}>
          <Img src={PillsBottle} width="40%" style={{ objectFit: 'contain', opacity: 0.7 }} />
          <Flex flexDirection={'column'} style={{ gap: 12 }}>
            <Skeleton width="220px" height="32px" />
            <Flex flexDirection={'column'}>
              <Skeleton width="100%" height="16px" />
              <Skeleton width="95%" height="16px" />
              <Skeleton width="100%" height="16px" />
              <Skeleton width="90%" height="16px" />
              <Skeleton width="95%" height="16px" />
              <Skeleton width="70%" height="16px" />
            </Flex>
          </Flex>
        </Flex>
      ))}
    </Flex>
  );
};

const GettingStartedSteps = ({ gettingStartedSteps }) => {
  if (!gettingStartedSteps || gettingStartedSteps.length < 1) return null;

  return (
    <Flex flexDirection={'column'}>
      <Heading mb={3}>Get Started Today</Heading>

      <Flex flexDirection={['column', 'row', 'row']} style={{ gap: 30, overflow: 'auto' }} mb={'20px'}>
        {gettingStartedSteps.map((x) => (
          <Flex style={{ gap: 12, flex: 1 }} flexDirection={'column'} key={`getStartedStep_${x.stepIndex}`}>
            <Text style={{ fontSize: 16, fontWeight: 600 }}>Step {x.stepIndex + 1}</Text>

            <Text>{x.description}</Text>
            {x.imageUrl && (
              <Flex flex={1} alignItems={'flex-end'}>
                <img src={x.imageUrl} width={200} />
              </Flex>
            )}
          </Flex>
        ))}
      </Flex>
    </Flex>
  );
};

const GettingStartedStepsSkeleton = () => {
  return (
    <Flex flexDirection={'column'}>
      <Skeleton width="220px" height="32px" />

      <Flex style={{ gap: 30, overflow: 'auto', padding: '0 0 20px 0' }}>
        {[0, 1, 2].map((x) => (
          <Flex style={{ gap: 12 }} flexDirection={'column'} key={`getStartedStep_${x.stepIndex}`}>
            <Skeleton width="100px" height="16px" />

            <Flex flexDirection={'column'}>
              <Skeleton width="80px" height="16px" />
              <Skeleton width="80px%" height="16px" />
              <Skeleton width="30px%" height="16px" />
            </Flex>
            <Flex flex={1} alignItems={'flex-end'}>
              <img src={PillsBottle} width={200} />
            </Flex>
          </Flex>
        ))}
      </Flex>
    </Flex>
  );
};

const RelatedProducts = ({ relatedProducts, history }) => {
  if (!relatedProducts || relatedProducts.length < 1) return null;

  return (
    <Flex flexDirection={'column'} style={{ gap: 20 }}>
      {relatedProducts.products.map((product) => (
        <PalomaRxProductCard key={product.id} product={product} history={history} />
      ))}
    </Flex>
  );
};

const ProductSpecifications = ({ productMetadata }) => {
  if (
    !productMetadata ||
    (!productMetadata.activeIngredients &&
      !productMetadata.inactiveIngredients &&
      !productMetadata.importantSafetyInformation &&
      !productMetadata.commonSideEffects &&
      !productMetadata.warnings &&
      !productMetadata.storage &&
      !productMetadata.manufacturer)
  )
    return null;
  const { activeIngredients, inactiveIngredients, importantSafetyInformation, commonSideEffects, warnings, storage, manufacturer } = productMetadata;
  const tabs = [];
  if ((activeIngredients && activeIngredients.length > 0) || (inactiveIngredients && inactiveIngredients.length > 0)) {
    tabs.push({
      id: 'ingredients',
      label: 'Ingredients',
      content: (
        <Flex flexDirection={'column'} style={{ gap: 20 }}>
          {activeIngredients &&
            activeIngredients.length > 0 && (
              <Flex flexDirection={'column'}>
                <h4>Active Ingredients:</h4>
                <ul>
                  {activeIngredients.map((x) => (
                    <li>{x}</li>
                  ))}
                </ul>
              </Flex>
            )}

          {inactiveIngredients && (
            <Flex flexDirection={'column'}>
              <h4>Inactive Ingredients:</h4>
              <ul>
                {inactiveIngredients.map((x) => (
                  <li>{x}</li>
                ))}
              </ul>
            </Flex>
          )}
        </Flex>
      ),
    });
  }
  if (commonSideEffects) {
    tabs.push({
      id: 'side-effects',
      label: 'Side Effects',
      content: (
        <Flex flexDirection={'column'}>
          <Text>Some patients may experience:</Text>
          <Text dangerouslySetInnerHTML={{ __html: commonSideEffects }} />
        </Flex>
      ),
    });
  }
  if (storage) {
    tabs.push({
      id: 'storage',
      label: 'Storage',
      content: (
        <Flex flexDirection={'column'}>
          <Text>Product storage information:</Text>
          <Text dangerouslySetInnerHTML={{ __html: storage }} />
        </Flex>
      ),
    });
  }
  if (manufacturer) {
    tabs.push({
      id: 'manufacturer',
      label: 'Manufacturer',
      content: (
        <Text>
          Manufactured by: <strong>{manufacturer}</strong>
        </Text>
      ),
    });
  }
  return (
    <>
      {tabs.length > 0 && <PalomRxProductTabControl tabs={tabs} />}
      {importantSafetyInformation && (
        <Flex flexDirection={'column'} style={{ gap: '10px' }}>
          <Heading style={{ display: 'flex', gap: 20, alignItems: 'center' }}>
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 16 16" fill="none">
              <path
                d="M8 1L2 3V9C2 12.3141 4.68594 15 8 15C11.3141 15 14 12.3141 14 9V3L8 1ZM12.875 9C12.875 11.6922 10.6922 13.875 8 13.875C5.30781 13.875 3.125 11.6922 3.125 9V3.84375L8 2.125L12.875 3.84375V9Z"
                fill="#8d4b3f"
              />
              <path
                d="M5.91271 7.42344C5.86057 7.37106 5.7986 7.32951 5.73036 7.30115C5.66212 7.27279 5.58895 7.25819 5.51505 7.25819C5.44115 7.25819 5.36798 7.27279 5.29974 7.30115C5.2315 7.32951 5.16953 7.37106 5.11739 7.42344C5.06502 7.47557 5.02346 7.53754 4.9951 7.60578C4.96675 7.67402 4.95215 7.74719 4.95215 7.82109C4.95215 7.89499 4.96675 7.96816 4.9951 8.03641C5.02346 8.10465 5.06502 8.16662 5.11739 8.21875L7.13927 10.2406L7.17208 10.2734C7.2214 10.3228 7.27997 10.362 7.34445 10.3888C7.40893 10.4155 7.47806 10.4293 7.54786 10.4293C7.61767 10.4293 7.68679 10.4155 7.75127 10.3888C7.81575 10.362 7.87433 10.3228 7.92364 10.2734L11.4158 6.78125C11.4652 6.73194 11.5044 6.67336 11.5312 6.60888C11.5579 6.5444 11.5717 6.47528 11.5717 6.40547C11.5717 6.33566 11.5579 6.26654 11.5312 6.20206C11.5044 6.13758 11.4652 6.079 11.4158 6.02969L11.3721 5.98594C11.3228 5.93653 11.2642 5.89733 11.1997 5.87059C11.1352 5.84384 11.0661 5.83008 10.9963 5.83008C10.9265 5.83008 10.8574 5.84384 10.7929 5.87059C10.7284 5.89733 10.6698 5.93653 10.6205 5.98594L7.54708 9.05781L5.91271 7.42344Z"
                fill="#8d4b3f"
              />
            </svg>
            Important Safety Information
          </Heading>

          <Flex style={{ marginLeft: 12, paddingLeft: 20, borderLeft: '2px solid #8d4b3f' }}>
            <Text dangerouslySetInnerHTML={{ __html: importantSafetyInformation }} />
          </Flex>
        </Flex>
      )}
      {warnings && (
        <Flex flexDirection={'column'} style={{ gap: '10px' }}>
          <Heading style={{ display: 'flex', gap: 20, alignItems: 'center' }}>
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 16 16" fill="none">
              <path
                d="M1.8168 14C1.69458 14 1.58347 13.9696 1.48347 13.9087C1.38347 13.8478 1.30569 13.7671 1.25013 13.6667C1.19458 13.5662 1.16413 13.4578 1.1588 13.3413C1.15347 13.2249 1.18391 13.1111 1.25013 13L7.4168 2.33333C7.48347 2.22222 7.56969 2.13889 7.67547 2.08333C7.78124 2.02778 7.88947 2 8.00013 2C8.1108 2 8.21924 2.02778 8.32547 2.08333C8.43169 2.13889 8.51769 2.22222 8.58347 2.33333L14.7501 13C14.8168 13.1111 14.8475 13.2251 14.8421 13.342C14.8368 13.4589 14.8061 13.5671 14.7501 13.6667C14.6941 13.7662 14.6164 13.8469 14.5168 13.9087C14.4172 13.9704 14.3061 14.0009 14.1835 14H1.8168ZM2.9668 12.6667H13.0335L8.00013 4L2.9668 12.6667ZM8.47547 11.8087C8.60302 11.6807 8.6668 11.5222 8.6668 11.3333C8.6668 11.1444 8.6028 10.9862 8.4748 10.8587C8.3468 10.7311 8.18858 10.6671 8.00013 10.6667C7.81169 10.6662 7.65347 10.7302 7.52547 10.8587C7.39747 10.9871 7.33347 11.1453 7.33347 11.3333C7.33347 11.5213 7.39747 11.6798 7.52547 11.8087C7.65347 11.9376 7.81169 12.0013 8.00013 12C8.18858 11.9987 8.34702 11.9353 8.47547 11.8087ZM8.47547 9.808C8.60302 9.68044 8.6668 9.52222 8.6668 9.33333V7.33333C8.6668 7.14444 8.6028 6.98622 8.4748 6.85867C8.3468 6.73111 8.18858 6.66711 8.00013 6.66667C7.81169 6.66622 7.65347 6.73022 7.52547 6.85867C7.39747 6.98711 7.33347 7.14533 7.33347 7.33333V9.33333C7.33347 9.52222 7.39747 9.68067 7.52547 9.80867C7.65347 9.93667 7.81169 10.0004 8.00013 10C8.18858 9.99956 8.34702 9.93556 8.47547 9.808Z"
                fill="#B8944F"
              />
            </svg>
            Warnings & Precautions
          </Heading>

          <Flex style={{ marginLeft: 12, paddingLeft: 20, borderLeft: '2px solid #B8944F' }}>
            <Text dangerouslySetInnerHTML={{ __html: warnings }} />
          </Flex>
        </Flex>
      )}
    </>
  );
};

const PalomaRxProductDetails = ({ session, match, history }) => {
  if (!session || !session.eligibility || !session.eligibility.canBookAsync) {
    history.replace('/');
    return null;
  }

  if (!match.params || !match.params.id) {
    history.replace('/rx-products');
    return null;
  }

  return (
    <>
      <Query query={GET_PALOMARX_PRODUCTS}>
        {({ loading: loadingProducts, data: dataProducts, error }) => {
          if (loadingProducts) return <LoadingScreen />;
          return (
            <Query query={GET_PALOMARX_PRODUCT_DETAILS} variables={{ productSku: match.params.id }}>
              {({ loading, data, error }) => {
                if (!!loading) return <LoadingScreen />;
                if (error) {
                  return (
                    <Flex flex={1} flexDirection={'column'} pt={5} style={{ gap: 28 }} justifyContent={'center'} alignItems={'center'}>
                      <Text fontSize={20}>An error occured</Text>
                      <Button onClick={() => history.push('/rx-products')} variant={'primary'}>
                        Go back to products list
                      </Button>
                    </Flex>
                  );
                }

                if (!data || !data.getPalomaRxProductDetails) {
                  return (
                    <Flex flex={1} flexDirection={'column'} pt={5} style={{ gap: 28 }} justifyContent={'center'} alignItems={'center'}>
                      <Text fontSize={20}>This product doesn't exist</Text>
                      <Button onClick={() => history.push('/rx-products')} variant={'primary'}>
                        Go back to products list
                      </Button>
                    </Flex>
                  );
                }
                let relatedProducts = [];

                let categoryIndex = 0;
                if (dataProducts && dataProducts.getPalomaRxCatalog) {
                  let allCategories = [...new Set(dataProducts.getPalomaRxCatalog.products.flatMap((item) => item.category))];
                  if (data.getPalomaRxProductDetails) {
                    categoryIndex = allCategories.findIndex((x) => x === data.getPalomaRxProductDetails.category);
                  }
                }

                if (
                  dataProducts &&
                  dataProducts.getPalomaRxCatalog &&
                  dataProducts.getPalomaRxCatalog.products &&
                  data.getPalomaRxProductDetails.similarProductIds &&
                  data.getPalomaRxProductDetails.similarProductIds.length > 0
                ) {
                  for (let index = 0; index < data.getPalomaRxProductDetails.similarProductIds.length; index++) {
                    const element = data.getPalomaRxProductDetails.similarProductIds[index];
                    let prd = dataProducts.getPalomaRxCatalog.products.find((x) => x.id === element);
                    if (!!prd) {
                      relatedProducts.push(prd);
                    }
                  }
                }
                return (
                  <Container>
                    <InnerContainer>
                      <svg style={{ position: 'absolute', top: '460px', left: 0 }} xmlns="http://www.w3.org/2000/svg" width="300" height="373" viewBox="0 0 394 373" fill="none">
                        <path
                          d="M169.332 -1.16533e-06C214.057 3.20311 255.335 15.1794 293.27 38.658C304.479 45.7663 316.042 52.309 327.914 58.261C373.341 80.4122 390.656 119.641 392.878 166.194C395.228 215.484 383.55 262.1 354.277 302.533C326.432 340.99 287.394 365.105 240.596 372.235C220.966 375.226 198.753 368.998 178.985 362.868C134.906 349.203 95.1818 326.559 57.2155 300.252C20.4574 274.77 4.35975 239.214 0.83649 197.213C-2.75872 154.4 5.41254 112.856 21.8877 73.5064C37.8968 35.2723 69.2056 14.4664 109.555 7.15687C129.255 3.58235 149.393 2.31053 169.332 -1.16533e-06Z"
                          fill="#EEF7FE"
                        />
                      </svg>

                      <svg style={{ position: 'absolute', bottom: '100px', right: 0 }} xmlns="http://www.w3.org/2000/svg" width="394" height="373" viewBox="0 0 394 373" fill="none">
                        <path
                          d="M169.332 -1.16533e-06C214.057 3.20311 255.335 15.1794 293.27 38.658C304.479 45.7663 316.042 52.309 327.914 58.261C373.341 80.4122 390.656 119.641 392.878 166.194C395.228 215.484 383.55 262.1 354.277 302.533C326.432 340.99 287.394 365.105 240.596 372.235C220.966 375.226 198.753 368.998 178.985 362.868C134.906 349.203 95.1818 326.559 57.2155 300.252C20.4574 274.77 4.35975 239.214 0.83649 197.213C-2.75872 154.4 5.41254 112.856 21.8877 73.5064C37.8968 35.2723 69.2056 14.4664 109.555 7.15687C129.255 3.58235 149.393 2.31053 169.332 -1.16533e-06Z"
                          fill="#ECB7AA"
                          fill-opacity="0.2"
                        />
                      </svg>
                      <A
                        onClick={() => {
                          history.push('/rx-products');
                        }}
                      >
                        {`< Back to Products`}
                      </A>
                      <Flex
                        flexDirection={'column'}
                        flex={1}
                        style={{
                          zIndex: 5,
                          gap: '60px',
                        }}
                      >
                        <PalomaRxProductDetailsInner
                          history={history}
                          details={data.getPalomaRxProductDetails}
                          categoryBadgeColors={PALOMA_PRODUCT_TAG_COLORS[(categoryIndex || 0) > PALOMA_PRODUCT_TAG_COLORS.length - 1 ? 0 : categoryIndex || 0]}
                        />
                        {/* <Faq details={data.getPalomaRxProductDetails.faq} /> */}
                        <SellingPoints sellingPoints={data.getPalomaRxProductDetails.sellingPoints} />
                        <GettingStartedSteps gettingStartedSteps={data.getPalomaRxProductDetails.getStartedSteps} />
                        <ProductSpecifications productMetadata={data.getPalomaRxProductDetails.productMetadata} />
                        {data.getPalomaRxProductDetails.productMetadata &&
                          data.getPalomaRxProductDetails.productMetadata.medicationGuideUrl && (
                            <Text
                              style={{}}
                              onClick={() => window.open(data.getPalomaRxProductDetails.productMetadata.medicationGuideUrl, '_blank')}
                            >
                              <Text style={{ fontWeight: 300, fontSize: '14px', textAlign: 'center' }}>
                                For complete prescribing and safety information, <span style={{ fontWeight: 500, textDecoration: 'underline', cursor: 'pointer' }}>view the Medication Guide</span>
                              </Text>
                            </Text>
                          )}

                        <Mutation
                          mutation={GET_VISITS_TOKEN}
                          onCompleted={(e) => {
                            if (e && e.getAsyncVisitToken) {
                              window.location = `${window._env_.REACT_APP_RX_VISITS_URL}/auth#token=${encodeURIComponent(e.getAsyncVisitToken)}&redirect=${data.getPalomaRxProductDetails.palomaSku}`;
                            }
                          }}
                        >
                          {(generateToken, { loading }) => (
                            <Button
                              onClick={generateToken}
                              disabled={!!loading}
                              variant={'primary'}
                              style={{
                                // padding: '8px 42px',
                                width: 'fit-content',
                                maxWidth: 'fit-content',
                                fontSize: 14,
                                fontWeight: 300,
                                alignSelf: 'center',
                              }}
                            >
                              Get Started
                            </Button>
                          )}
                        </Mutation>
                        <Faq2 details={data.getPalomaRxProductDetails.faq} />
                        <RelatedProducts relatedProducts={relatedProducts} history={history} />
                      </Flex>
                    </InnerContainer>
                  </Container>
                );
              }}
            </Query>
          );
        }}
      </Query>
    </>
  );
};

const LoadingScreen = () => {
  return (
    <Container>
      <InnerContainer>
        <svg style={{ position: 'absolute', top: '460px', left: 0 }} xmlns="http://www.w3.org/2000/svg" width="394" height="373" viewBox="0 0 394 373" fill="none">
          <path
            d="M169.332 -1.16533e-06C214.057 3.20311 255.335 15.1794 293.27 38.658C304.479 45.7663 316.042 52.309 327.914 58.261C373.341 80.4122 390.656 119.641 392.878 166.194C395.228 215.484 383.55 262.1 354.277 302.533C326.432 340.99 287.394 365.105 240.596 372.235C220.966 375.226 198.753 368.998 178.985 362.868C134.906 349.203 95.1818 326.559 57.2155 300.252C20.4574 274.77 4.35975 239.214 0.83649 197.213C-2.75872 154.4 5.41254 112.856 21.8877 73.5064C37.8968 35.2723 69.2056 14.4664 109.555 7.15687C129.255 3.58235 149.393 2.31053 169.332 -1.16533e-06Z"
            fill="#EEF7FE"
          />
        </svg>

        <svg style={{ position: 'absolute', bottom: '100px', right: 0 }} xmlns="http://www.w3.org/2000/svg" width="394" height="373" viewBox="0 0 394 373" fill="none">
          <path
            d="M169.332 -1.16533e-06C214.057 3.20311 255.335 15.1794 293.27 38.658C304.479 45.7663 316.042 52.309 327.914 58.261C373.341 80.4122 390.656 119.641 392.878 166.194C395.228 215.484 383.55 262.1 354.277 302.533C326.432 340.99 287.394 365.105 240.596 372.235C220.966 375.226 198.753 368.998 178.985 362.868C134.906 349.203 95.1818 326.559 57.2155 300.252C20.4574 274.77 4.35975 239.214 0.83649 197.213C-2.75872 154.4 5.41254 112.856 21.8877 73.5064C37.8968 35.2723 69.2056 14.4664 109.555 7.15687C129.255 3.58235 149.393 2.31053 169.332 -1.16533e-06Z"
            fill="#ECB7AA"
            fill-opacity="0.2"
          />
        </svg>
        <Flex
          flexDirection={'column'}
          flex={1}
          style={{
            zIndex: 5,
            gap: '60px',
          }}
        >
          <PalomaRxProductDetailsInnerSkeleton />
          {/* <FaqSkeleton /> */}
          <SellingPointsSkeleton />
          <GettingStartedStepsSkeleton />
        </Flex>
      </InnerContainer>
    </Container>
  );
};

// export default compose(
//   withSession,
//   withRouter,
// )(PalomaRxProductDetails, { loadingScreen: <LoadingScreen /> });
export default withSession(withRouter(PalomaRxProductDetails), { loadingScreen: <LoadingScreen /> });
